import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { translate as translateConstructor } from '@haldor/ui';
import { Box, Alert, Button } from '@mui/material';
import DisplayForm from 'containers/FormTemplate/Display/DisplayForm';
import { createForm } from 'actions/templates';
import { getMyMentorGroups } from 'actions/groups';
import { Spinner } from 'UI';
import Moment from 'moment';

const DATE_FORMAT = 'YYYY-MM-DD';
const DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm';

const toUtcIsoString = (dateValue, boundaryType) => {
	if (!dateValue || typeof dateValue !== 'string') return dateValue;

	let m;
	if (dateValue.includes('T')) {
		m = Moment(dateValue, [DATE_TIME_FORMAT, Moment.ISO_8601], true);
	} else {
		m = Moment(dateValue, DATE_FORMAT, true);
		if (m.isValid()) {
			if (boundaryType === 'end') {
				m = m.clone().hours(23).minutes(59).seconds(0);
			} else {
				m = m.clone().hours(0).minutes(0).seconds(0);
			}
		}
	}

	if (!m || !m.isValid()) return dateValue;
	return m.clone().utc().toISOString();
};

const normalizeLeaveDateRangeValue = (value) => {
	if (typeof value !== 'string') return value;

	let parsed;
	try {
		parsed = JSON.parse(value);
	} catch (e) {
		return value;
	}

	if (!parsed || typeof parsed !== 'object' || !('start' in parsed) || !('end' in parsed)) {
		return value;
	}

	const normalized = {
		...parsed,
		start: toUtcIsoString(parsed.start, 'start') || '',
		end: toUtcIsoString(parsed.end, 'end') || '',
	};

	return JSON.stringify(normalized);
};

const LeaveRequestForm = ({ studentId: propStudentId = null, guardianId: propGuardianId = null, onSuccess, onAbort }) => {
	const dispatch = useDispatch();
	const translations = useSelector(state => state.Languages.translations);
	const translate = translateConstructor(translations);

	const currentUser = useSelector(state => state.user.currentUser);
	const studentId = propStudentId || (currentUser && (currentUser.id || currentUser.userId));

	const mentorGroupsFromStore = useSelector(state => state.sectionsV2?.myMentorGroups || state.groups?.mentorGroups || []);
	const [mentorGroups, setMentorGroups] = useState(mentorGroupsFromStore || []);
	const [loadingGroups, setLoadingGroups] = useState(false);

	useEffect(() => {
		let active = true;
		const fetchGroups = async () => {
			try {
				setLoadingGroups(true);
				const result = await dispatch(getMyMentorGroups());
				if (!active) return;
				setMentorGroups(Array.isArray(result) ? result : (result ? [result] : []));
			} catch (e) {
				if (active) setMentorGroups([]);
			} finally {
				if (active) setLoadingGroups(false);
			}
		};

		fetchGroups();
		return () => { active = false; };
	}, [dispatch]);

	const showMentorInfo = !loadingGroups && Array.isArray(mentorGroups) && mentorGroups.length === 0;

	const handleSubmit = (data) => {
		const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
		const clientUtcOffsetMinutes = -new Date().getTimezoneOffset();
		const normalizedFieldResults = (data.fieldResults || []).map((fieldResult) => ({
			...fieldResult,
			value: normalizeLeaveDateRangeValue(fieldResult.value),
		}));

		const formInstance = {
			...data,
			fieldResults: normalizedFieldResults,
			clientTimeZone,
			clientUtcOffsetMinutes,
		}

		const formPayload = {
			title: 'Leave Request',
			formInstances: [formInstance],
			type: 'LEAVE_REQUEST',
			clientTimeZone,
			clientUtcOffsetMinutes,
		};

		return dispatch(createForm(formPayload)).then((result) => {
			if (onSuccess) onSuccess(result);
			return result;
		});
	};

	if (loadingGroups) {
		return (
			<Box sx={{ p: 2 }}>
				<Spinner center />
			</Box>
		);
	}

	if (showMentorInfo) {
		return (
			<Box>
				<Alert severity="info" sx={{ mb: 2 }}>
					{translate("To create a leave application, the student must belong to a mentor group. Please contact your school.")}
				</Alert>
				<Button variant="outlined" onClick={onAbort || (() => { })}>{translate('Close')}</Button>
			</Box>
		);
	}

	return (
		<div className="form-container">
			<DisplayForm
				type="LEAVE_REQUEST"
				onCancel={onAbort}
				onSubmit={handleSubmit}
			/>
		</div>
	);
};

export default LeaveRequestForm;
